import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../cn';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning';
export type ToastInput = {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** ms before auto-dismiss; 0 keeps it until closed. Default 5000 (errors 8000). */
  duration?: number;
  action?: { label: string; onClick: () => void };
};
type ToastItem = ToastInput & { id: number };

type ToastApi = {
  toast: (t: ToastInput) => number;
  success: (title: string, description?: string) => number;
  error: (title: string, description?: string) => number;
  dismiss: (id: number) => void;
};

const ToastContext = React.createContext<ToastApi | null>(null);

/** Wrap the app once; then `const { success, error } = useToast()`. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const nextId = React.useRef(1);
  const timers = React.useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = React.useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const toast = React.useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      setItems((list) => [...list.slice(-4), { ...input, id }]);
      const duration = input.duration ?? (input.variant === 'error' ? 8000 : 5000);
      if (duration > 0) timers.current.set(id, setTimeout(() => dismiss(id), duration));
      return id;
    },
    [dismiss],
  );

  React.useEffect(() => {
    const map = timers.current;
    return () => map.forEach((t) => clearTimeout(t));
  }, []);

  const api = React.useMemo<ToastApi>(
    () => ({
      toast,
      dismiss,
      success: (title, description) => toast({ title, description, variant: 'success' }),
      error: (title, description) => toast({ title, description, variant: 'error' }),
    }),
    [toast, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2">
        {items.map((t) => (
          <ToastView key={t.id} item={t} onClose={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const Icon = { default: Info, success: CheckCircle2, error: XCircle, warning: TriangleAlert }[item.variant ?? 'default'];
  const color = { default: 'text-info', success: 'text-success', error: 'text-destructive', warning: 'text-warning' }[item.variant ?? 'default'];
  return (
    <div role={item.variant === 'error' ? 'alert' : 'status'} className="pointer-events-auto flex items-start gap-3 rounded-2xl bg-popover/95 p-4 text-popover-foreground shadow-pop backdrop-blur-xl animate-fade-in">
      <Icon className={cn('mt-0.5 size-5 shrink-0', color)} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{item.title}</p>
        {item.description ? <p className="mt-0.5 text-sm text-muted-foreground">{item.description}</p> : null}
        {item.action ? (
          <button
            type="button"
            className="mt-2 text-sm font-medium underline-offset-4 hover:underline"
            onClick={() => {
              item.action!.onClick();
              onClose();
            }}
          >
            {item.action.label}
          </button>
        ) : null}
      </div>
      <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Dismiss">
        <X className="size-4" />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
